/**
 * Time in the app, split three ways — the pure half of the usage tracker.
 *
 * `docs/plans/usage-tracking.md`. **Active** is visible with input in the last
 * five minutes; **watching** is visible without it — a live chart being read
 * is use, not idleness; **background** is hidden or minimised.
 *
 * Accounted from timestamps, never by counting ticks: a hidden tab's timers
 * are throttled to a minute or more, and a sleeping laptop's stop altogether,
 * so a count of ticks is a count of whatever the browser allowed. Each span is
 * split at the moment input went stale, so a tick that arrives late still
 * books the right seconds to each state. The server clamps what one beat may
 * claim; the client does not, so the clamp is in one place.
 */

/** Input older than this and a visible tab is watching rather than active. */
export const IDLE_MS = 5 * 60 * 1000;

/** How often the open tab reports (owner, 2026-10-14). */
export const BEAT_MS = 5 * 60 * 1000;

export interface Totals {
  active: number;
  watching: number;
  background: number;
}

export class ActivityClock {
  private last: number;
  private visible: boolean;
  private lastInput: number;
  private ms: Totals = { active: 0, watching: 0, background: 0 };

  constructor(now: number, visible: boolean) {
    this.last = now;
    this.visible = visible;
    // Opening the app is itself a gesture: the first five minutes are active.
    this.lastInput = now;
  }

  /** Book the span since the last call to the state it was in. */
  advance(now: number): void {
    if (now <= this.last) return;
    const from = this.last;
    this.last = now;
    if (!this.visible) {
      this.ms.background += now - from;
      return;
    }
    const staleAt = this.lastInput + IDLE_MS;
    const activeUntil = Math.min(now, Math.max(from, staleAt));
    this.ms.active += activeUntil - from;
    this.ms.watching += now - activeUntil;
  }

  /** Something was typed, clicked, touched or scrolled. */
  input(now: number): void {
    this.advance(now);
    this.lastInput = now;
  }

  /** The tab was shown or hidden. */
  setVisible(now: number, visible: boolean): void {
    this.advance(now);
    this.visible = visible;
  }

  /**
   * Whole seconds since the last take, and reset. The sub-second remainder
   * stays behind, so nothing is lost to rounding over a long session.
   */
  take(now: number): Totals {
    this.advance(now);
    const out: Totals = {
      active: Math.floor(this.ms.active / 1000),
      watching: Math.floor(this.ms.watching / 1000),
      background: Math.floor(this.ms.background / 1000),
    };
    this.ms.active -= out.active * 1000;
    this.ms.watching -= out.watching * 1000;
    this.ms.background -= out.background * 1000;
    return out;
  }
}

/** A random id for a session or a device: 32 hex characters. */
export function mintId(random: (bytes: Uint8Array) => Uint8Array = (b) => crypto.getRandomValues(b)): string {
  return Array.from(random(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");
}

const DEVICE_KEY = "qf.deviceId";

/**
 * This browser's device id — minted once and kept.
 *
 * No fingerprinting: a cleared browser is a new device, and that is the honest
 * answer. Storage that throws (a private window, blocked site data) gets a
 * fresh id per load, which over-counts devices rather than failing to report.
 */
export function deviceId(storage: Pick<Storage, "getItem" | "setItem"> | null = safeStorage()): string {
  try {
    const held = storage?.getItem(DEVICE_KEY);
    if (held && /^[A-Za-z0-9_-]{8,64}$/.test(held)) return held;
  } catch {
    /* fall through to a fresh one */
  }
  const minted = mintId();
  try {
    storage?.setItem(DEVICE_KEY, minted);
  } catch {
    /* per load, then */
  }
  return minted;
}

function safeStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

// ── display ─────────────────────────────────────────────────────────────────

/** `0m`, `45m`, `3h05`, `120h45` — hours never roll into days, so columns compare. */
export function formatDuration(seconds: number): string {
  const minutes = Math.floor(Math.max(0, seconds) / 60);
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h${String(minutes % 60).padStart(2, "0")}`;
}

/**
 * `Today 09:14`, `Yesterday 21:02`, `10-05 08:30`, `2025-12-31` — in the
 * viewer's own clock. A bare `YYYY-MM-DD` (a day known only from the daily
 * totals) is shown as the day and nothing more.
 */
export function formatWhen(iso: string | null, now: Date = new Date()): string {
  if (!iso) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, "0");
  const time = `${pad(at.getHours())}:${pad(at.getMinutes())}`;
  const day = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (day(at) === day(now)) return `Today ${time}`;
  if (day(at) === day(yesterday)) return `Yesterday ${time}`;
  if (at.getFullYear() === now.getFullYear()) return `${pad(at.getMonth() + 1)}-${pad(at.getDate())} ${time}`;
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
}

/** `Chrome · Windows · desktop`, skipping what is unknown. */
export function deviceLabel(d: { browser: string | null; os: string | null; deviceType: string | null }): string {
  return [d.browser, d.os, d.deviceType].filter(Boolean).join(" · ") || "Unknown device";
}

/** `San Jose, US`; empty when the place is unknown. */
export function placeLabel(city: string | null, country: string | null): string {
  return [city, country].filter(Boolean).join(", ");
}

/**
 * `1920×1080 ·1.25 · win 1280×640` — the display, its pixel ratio, and the
 * smallest window the session had. Empty for a session before 2026-10-31.
 */
export function screenLabel(s: {
  screenW?: number | null;
  screenH?: number | null;
  pixelRatio?: number | null;
  windowW?: number | null;
  windowH?: number | null;
}): string {
  const parts: string[] = [];
  if (s.screenW && s.screenH) parts.push(`${s.screenW}×${s.screenH}`);
  if (s.pixelRatio) parts.push(`·${Number(s.pixelRatio.toFixed(2))}`);
  if (s.windowW && s.windowH) parts.push(`· win ${s.windowW}×${s.windowH}`);
  return parts.join(" ");
}
